import * as Yup from "yup"
import { useEffect, useMemo, useState } from "react"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"

import useGetDeviceType from "../../../../hooks/useGetDeviceType"
import SearchInputV2 from "../../../SearchInputV2/SearchInputV2"
import MuiAutoComplete from "../../../MuiAutocomplete"
import { SitesObj, useLazyGetAllSitesQuery } from "../../../../pages/Sites/SiteService"
import {
    useGetGsrTemplateQuery,
    useParseDrMutation,
    useRaiseSoloLogisticsMutation,
} from "../../../../pages/Logistics/LogisticsServices"
import {
    useGetCitiesQuery,
    useGetStandardItemsQuery,
    useGetStatesQuery,
    useGetTimeOfDayQuery,
} from "../../../../pages/Common/commonService"
import { TStore } from "../../../../store/Store"
import { IMaterialDetail, IMaterialDispatchForm, setMaterialDispatchForm } from "../../RaiseTicketFormSlice"
import { DropdownObj } from "../../../../pages/Common/commonTypes"
import { EFormModule, FormPageProps } from "../.."
import { DEAL_TYPE } from "../../config/projectFormConstants"

import delete_red from "../../../../assets/images/delete_red.svg"

const validationSchema: Yup.ObjectSchema<IMaterialDispatchForm> = Yup.object({
    project_name: Yup.string().required("Project Name is required"),
    visit_by_date: Yup.string().required("Visit By is required"),
    visit_by_time: Yup.object().shape({
        id: Yup.number().required("Time of Day is required"),
        title: Yup.string().required("Time of Day is required"),
    }),
    source_site_id: Yup.object().shape({
        id: Yup.number().required("Source Site Id is required"),
        title: Yup.string().required("Source Site Id is required"),
    }),
    source_address: Yup.string().required("Source Address is required"),
    source_pincode: Yup.string().required("Pincode is required"),
    source_state: Yup.object().shape({
        id: Yup.number().required("State is required"),
        title: Yup.string().required("State is required"),
    }),
    source_latitude: Yup.string().notRequired(),
    source_longitude: Yup.string().notRequired(),
    source_city: Yup.object().shape({
        id: Yup.number().required("City is required"),
        title: Yup.string().required("City is required"),
    }),
    source_poc: Yup.object().shape({
        name: Yup.string().required("Name is required"),
        phone: Yup.string().required("Phone is required"),
        email: Yup.string().email("Invalid email format").required("Email is required"),
    }),
    destination_poc: Yup.object().shape({
        name: Yup.string().required("Name is required"),
        phone: Yup.string().required("Phone is required"),
        email: Yup.string().email("Invalid email format").required("Email is required"),
    }),
    file: Yup.string(),
    materials: Yup.array()
        .min(1, "At least one material is required")
        .of(
            Yup.object().shape({
                description: Yup.string().required("Description is required"),
                quantity: Yup.number()
                    .required("Quantity is required")
                    .min(0.5, "Quantity should be greater than 0.5"),
                remarks: Yup.string().nullable(),
                scope: Yup.string().required("Scope is required"),
                item_code: Yup.string().required("SKU Code is required"),
            })
        ),
})

const useMaterialDispatch = ({ handleNextStep }: FormPageProps) => {
    const dispatch = useDispatch()
    const [sitesPage, setSitesPage] = useState(1)
    const [totalPages, setTotalPages] = useState<number | null>(null)
    const [sitesList, setSitesList] = useState<SitesObj[]>([])
    const [searchTerm, setSearchTerm] = useState("")
    const [isSitesLoading, setIsSitesLoading] = useState(false)
    const {
        material_dispatch_form,
        form_module,
        deal_sow_form: { deal_type },
        sites_form,
    } = useSelector((state: TStore) => state.RaiseTicketForm)

    const { deviceType } = useGetDeviceType()

    const materialDispatchForm = useFormik({
        initialValues: material_dispatch_form,
        validationSchema,
        onSubmit: handleSubmit,
    })

    const showError = materialDispatchForm.submitCount > 0

    const { data: gsrTemplate } = useGetGsrTemplateQuery()
    const { data: standardItemsList } = useGetStandardItemsQuery()
    const { data: timeOfDayList } = useGetTimeOfDayQuery()
    const { data: stateList } = useGetStatesQuery()
    const { data: cityList } = useGetCitiesQuery(
        { state_id: [materialDispatchForm.values.source_state?.id!] },
        {
            skip: !materialDispatchForm.values.source_state?.id,
        }
    )
    const [getAllSites] = useLazyGetAllSitesQuery()
    const [parseDr, { isLoading: isUploadingDr }] = useParseDrMutation()
    const [raiseSoloLogistics, { isLoading: isRaisingSoloLogistics }] = useRaiseSoloLogisticsMutation()

    const standardItemOptions = useMemo(() => {
        return standardItemsList?.map(i => ({ id: i.id, title: i.item_code, name: i.item_description }))
    }, [standardItemsList])

    const timeOfDayOptions = useMemo(() => {
        return timeOfDayList?.data?.map(i => ({ id: i.id, title: i.name }))
    }, [timeOfDayList])

    const siteOptions = useMemo(() => {
        return sitesList.map(site => ({
            id: site.id,
            title: site.name,
        }))
    }, [sitesList])

    const handleTableValueChange = (key: keyof IMaterialDetail, value: string | DropdownObj, index: number) => {
        if (value === null) {
            materialDispatchForm.setFieldValue(`materials.${index}.${key}`, "")
            return
        }
        // Update the materialDispatchForm values
        if (key === "item_code" && standardItemOptions && typeof value === "object") {
            materialDispatchForm.setFieldValue(`materials.${index}.${key}`, value.title)
            const description = standardItemOptions.find(i => i.title === value.title)?.name
            const itemId = standardItemOptions.find(i => i.title === value.title)?.id
            materialDispatchForm.setFieldValue(`materials.${index}.item_id`, itemId)
            materialDispatchForm.setFieldValue(`materials.${index}.description`, description)
        } else if (key === "scope" && typeof value === "object") {
            materialDispatchForm.setFieldValue(`materials.${index}.${key}`, value.title)
        } else materialDispatchForm.setFieldValue(`materials.${index}.${key}`, value)
    }

    const handleAddMaterialRow = () => {
        const currentMaterials = materialDispatchForm.values.materials || []
        const newMaterial: IMaterialDetail = {
            item_code: "",
            description: "",
            quantity: 0,
            remarks: "",
            scope: "",
        }
        const updatedMaterials = [...currentMaterials, newMaterial]
        materialDispatchForm.setFieldValue("materials", updatedMaterials)
    }

    const materialTableHeaders = [
        {
            title: "S.No.",
        },
        {
            title: "SKU Code",
        },
        {
            title: "Description",
            minWidth: "250px",
        },
        {
            title: "Scope",
        },
        {
            title: "Quantity",
        },
        {
            title: "Remarks",
        },
        {
            title: "",
        },
    ]

    const materialRowData = materialDispatchForm.values.materials?.map((charger: any, index: number) => {
        return {
            "S.No.": { value: index + 1 },
            "SKU Code": {
                value: (
                    <MuiAutoComplete
                        placeholder="Select SKU Code"
                        options={standardItemOptions || []}
                        value={
                            charger?.item_id
                                ? {
                                      id: charger.item_id,
                                      title: charger.item_code,
                                  }
                                : null
                        }
                        onChange={(value: any) => {
                            handleTableValueChange("item_code", value, index)
                        }}
                        error={!charger.item_code && showError}
                    />
                ),
            },
            Description: {
                value: (
                    <div style={{ width: "250px" }}>
                        <SearchInputV2
                            fontsize="10px"
                            placeholder={"Enter Description"}
                            searchIcon={false}
                            onSearch={(value: string) => {
                                handleTableValueChange("description", value, index)
                            }}
                            disabled
                            value={charger.description}
                            height="24px"
                            error={!charger.description}
                            showError={showError}
                        />
                    </div>
                ),
            },
            Scope: {
                value: (
                    <MuiAutoComplete
                        placeholder="Select Scope"
                        options={[
                            { id: "Statiq", title: "Statiq" },
                            { id: "Vendor", title: "Vendor" },
                        ]}
                        value={
                            charger.scope
                                ? {
                                      id: charger.scope,
                                      title: charger.scope,
                                  }
                                : null
                        }
                        onChange={(value: any) => {
                            handleTableValueChange("scope", value, index)
                        }}
                        error={!charger.scope && showError}
                    />

                    // <div style={{ width: "93px" }}>
                    //     <MultiSelectDropdown
                    //         fontsize="10px"
                    //         title="Select"
                    //         options={[
                    //             { id: "Statiq", title: "Statiq" },
                    //             { id: "Vendor", title: "Vendor" },
                    //         ]}
                    //         id="City"
                    //         onChange={() => {}}
                    //         selectedOptions={charger.scope ? [{ id: charger.scope, title: charger.scope }] : []}
                    //         selectionType={"single"}
                    //         onSelect={(value: DropdownObj) => {
                    //             handleTableValueChange("scope", value, index)
                    //         }}
                    //         height="24px"
                    //         error={!charger.scope}
                    //         showError={showError}
                    //     />
                    // </div>
                ),
            },
            Quantity: {
                value: (
                    <div style={{ width: "93px" }}>
                        <SearchInputV2
                            fontsize="10px"
                            placeholder={"Quantity"}
                            searchIcon={false}
                            onSearch={(value: string) => {
                                handleTableValueChange("quantity", value, index)
                            }}
                            value={charger.quantity}
                            height="24px"
                            error={(materialDispatchForm.errors.materials?.[index] as any)?.quantity}
                            showError={showError}
                        />
                    </div>
                ),
            },
            Remarks: {
                value: (
                    <div style={{ width: "93px" }}>
                        <SearchInputV2
                            fontsize="10px"
                            placeholder={"Enter Remarks"}
                            searchIcon={false}
                            onSearch={(value: string) => {
                                handleTableValueChange("remarks", value, index)
                            }}
                            value={charger?.remarks || ""}
                            height="24px"
                        />
                    </div>
                ),
            },
            "": {
                value: (
                    <>
                        <img src={delete_red} alt="delete" onClick={() => handleDeleteMaterialRow(index)} />
                    </>
                ),
            },
        }
    })

    const handleDeleteMaterialRow = (index: number) => {
        const updatedMaterials = materialDispatchForm.values?.materials?.filter((_, i) => i !== index)
        materialDispatchForm.setFieldValue("materials", updatedMaterials)
    }

    const fetchSites = async (page: number) => {
        if (isSitesLoading) return
        if (totalPages !== null && page > totalPages) return
        setIsSitesLoading(true)
        try {
            const res = await getAllSites({
                page: page,
                per_page: 20,
                site: searchTerm,
            }).unwrap()

            if (res) {
                setSitesList(prev => [...prev, ...(res.data || [])])
                setTotalPages(res.totalPages || 0)
            }
        } catch (error) {
            console.error("Error fetching sites:", error)
            setSitesList([])
            setTotalPages(null)
            return
        } finally {
            setIsSitesLoading(false)
        }
    }

    const handleSearchSites = async (searchTerm: string) => {
        if (isSitesLoading) return
        setIsSitesLoading(true)
        setSearchTerm(searchTerm)
        try {
            const res = await getAllSites({
                page: 1,
                per_page: 20,
                site: searchTerm,
            }, true).unwrap()
            if (res) {
                setSitesList(res.data || [])
                setTotalPages(res.totalPages || 0)
            }
        } catch (error) {
            console.error("Error searching sites:", error)
            setSitesList([])
            setTotalPages(null)
        } finally {
            setIsSitesLoading(false)
        }
    }

    const handleLoadMoreSites = () => {
        setSitesPage(prev => {
            const nextPage = prev + 1
            fetchSites(nextPage)
            return nextPage
        })
    }

    const handleSiteChange = async (site: DropdownObj) => {
        const selectedSite = sitesList.find(s => s.id === site.id)
        materialDispatchForm.setFieldValue("source_site_id", site)
        materialDispatchForm.setFieldValue("source_address", selectedSite?.address)
        materialDispatchForm.setFieldValue("source_pincode", selectedSite?.pincode)
        materialDispatchForm.setFieldValue("source_state", {
            id: selectedSite?.state_id,
            title: stateList?.find(s => s.id === selectedSite?.state_id)?.name,
        })
        materialDispatchForm.setFieldValue("source_latitude", selectedSite?.latitude)
        materialDispatchForm.setFieldValue("source_longitude", selectedSite?.longitude)
    }

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const formData = new FormData()
            if (e.target.files) {
                formData.append("file", e.target.files[0])
            }
            const response = await parseDr(formData).unwrap()
            if (response) {
                materialDispatchForm.setFieldValue("file", e.target.files![0].name)
                materialDispatchForm.setFieldValue("materials", response)
            }
        } catch (error) {
            console.error("Error parsing file:", error)
            throw error
        }
    }

    async function handleSubmit(values: IMaterialDispatchForm) {
        if (form_module === EFormModule.LOGISTICS) {
            if (deal_type.title === DEAL_TYPE.CPO) {
                const sourceObj = {
                    site_id: values.source_site_id.id,
                    address: values.source_address,
                    latitude: values.source_latitude,
                    longitude: values.source_longitude,
                    pincode: values.source_pincode,
                    state_id: values.source_state.id,
                    city_id: values.source_city.id,
                }
                const destinationObj = {
                    site_id: sites_form.siteId,
                    address: sites_form.siteAddress,
                    latitude: sites_form.latitude,
                    longitude: sites_form.longitude,
                    pincode: sites_form.pincode,
                    state_id: sites_form.state,
                    city_id: sites_form.city,
                }
                const formData = new FormData()
                formData.append("deal_type", deal_type.title)
                formData.append("project_name", values.project_name)
                formData.append("source_details", JSON.stringify(sourceObj))
                formData.append("materials", JSON.stringify(values.materials))
                formData.append("visit_by_date", values.visit_by_date)
                formData.append("visit_by_time", values.visit_by_time.id?.toString())
                formData.append("source_poc", JSON.stringify(values.source_poc))
                formData.append("destination_poc", JSON.stringify(values.destination_poc))
                formData.append("destination_details", JSON.stringify(destinationObj))
                await raiseSoloLogistics(formData)
            } else {
                dispatch(setMaterialDispatchForm(values))
            }
        }
        handleNextStep()
    }

    useEffect(() => {
        if (cityList?.length === 0 || !materialDispatchForm.values.source_state) return
        const selectedSite = sitesList.find(s => s.id === materialDispatchForm.values.source_site_id.id)
        if (!selectedSite) return
        materialDispatchForm.setFieldValue("source_city", {
            id: selectedSite?.city_id,
            title: cityList?.find(c => c.id === selectedSite?.city_id)?.name,
        })
    }, [materialDispatchForm.values.source_state, cityList])

    useEffect(() => {
        if (sitesList.length === 0) {
            fetchSites(1)
        }
    }, [sitesList.length])

    return {
        states: {
            materialDispatchForm,
            timeOfDayOptions,
            stateList,
            cityList,
            gsrTemplate,
            materialTableHeaders,
            deviceType,
            sitesList,
            isSitesLoading,
            siteOptions,
            materialRowData,
            isUploadingDr,
            isRaisingSoloLogistics,
        },
        functions: {
            handleLoadMoreSites,
            handleSearchSites,
            handleSiteChange,
            handleFileUpload,
            handleAddMaterialRow,
        },
    }
}

export default useMaterialDispatch
